import Immutable from 'immutable';

import type { ActionType } from 'sharedreact/actions/generic/actions';
import { FilterMode } from 'sharedreact/constants/generic/Constants';
import type Filter from 'sharedreact/data/Filter';
import SortOrder from 'sharedreact/data/SortOrder';
import User from 'sharedreact/data/User';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import AuthenticationStore from 'sharedreact/stores/AuthenticationStore';
import type ItemsState from 'sharedreact/stores/generic/ItemsState';
import PaginatedItemsStore from 'sharedreact/stores/generic/PaginatedItemsStore';
import type { RangeType } from 'sharedreact/stores/generic/Type';

import UsersActions from 'workspaces/common/actions/UsersActions';
import UsersConstants from 'workspaces/common/constants/UsersConstants';

class UsersStore extends PaginatedItemsStore<User> {
  _useOnlyNamesDefaultFilter: boolean;
  _useOnlyNamesDefaultFilterGallery: boolean;
  _useOnlyNamesDefaultFilterTemp: boolean;

  constructor () {
    super(AppDispatcher, UsersActions, UsersConstants);
    this._useOnlyNamesDefaultFilter = false;
    this._useOnlyNamesDefaultFilterGallery = false;
    this._useOnlyNamesDefaultFilterTemp = true;
  }

  getInitialSortOrder (): SortOrder {
    return new SortOrder({keyword: 'username', ascending: true});
  }

  getSecondarySortKeyword (): string {
    return 'id';
  }

  getFilterKeywords (): string[] {
    let filterKeywords = ['username', 'fullname'];
    if (!this._useOnlyNamesDefaultFilter) {
      filterKeywords = filterKeywords.concat('email');
    }
    return filterKeywords;
  }

  getSortKeywords (): string[] {
    let filterKeywords = ['username', 'fullname'];
    if (!this._useOnlyNamesDefaultFilter) {
      filterKeywords = filterKeywords.concat('email');
    }
    return filterKeywords;
  }

  getItemKeyForKeyword (keyword: string): string | null {
    switch (keyword) {
      case 'username':
        return 'name';
      case 'fullname':
        return 'fullName';
      case 'role':
      case 'email':
      case 'domain':
      default:
        return keyword;
    }
  }

  // some user object might contain a userInfoId as id (for example for users in Todos)
  // so override the behavior of getting items by id and checking ids against selection

  getItem (id: string): User | undefined {
    let user: User | undefined = this.getState().mappedItems.get(id);
    if (user == null) {
      user = this.getState().mappedItems.find((item) => item.userInfoId === id);
    }

    return user ?? undefined;
  }

  _isSelected (item: User | null, selection: Immutable.Set<string>): boolean {
    return item != null && (selection.includes(item.id) || selection.includes(item.userInfoId));
  }

  _matchesId (item: User | null, id: string): boolean {
    return item != null && (item.id === id || item.userInfoId === id);
  }

  // override to slip in the useOnlyNamesDefaultFilter
  _getAll (
    state: ItemsState<User>,
    range: RangeType,
    filter: Filter,
    advancedFilter: Filter,
    groupBy: SortOrder,
    sortOrder: SortOrder
  ): void {
    this.Actions.fetch(range, filter, advancedFilter, this._useOnlyNamesDefaultFilter, groupBy, sortOrder);
  }

  _find (
    ids: Immutable.Set<string>,
    filter: Filter,
    advancedFilter: Filter,
    groupBy: SortOrder,
    sortOrder: SortOrder
  ): void {
    if (this.Actions.find != null) {
      this.Actions.find(ids, filter, advancedFilter, this._useOnlyNamesDefaultFilter, groupBy, sortOrder);
    }
  }

  override reduce (state: ItemsState<User | null>, action: ActionType): ItemsState<User | null> {
    switch (action.actionType) {
      case UsersConstants.SAVE_SUCCESS: {
        if (AuthenticationStore.isLoggedIn()) {
          return super.reduce(state, action);
        } else {
          return state;
        }
      }
      case this.Constants.SET_FILTER:
        if (action.useOnlyNamesDefaultFilter != null) {
          if (action.filterMode === FilterMode.GALLERY) {
            this._useOnlyNamesDefaultFilterGallery = action.useOnlyNamesDefaultFilter;
          } else if (action.filterMode === FilterMode.TEMP) {
            this._useOnlyNamesDefaultFilterTemp = action.useOnlyNamesDefaultFilter;
          }
        }
        return super.reduce(state, action);
      case UsersConstants.ACTIVATE_FILTER:
        if (action.filterMode === FilterMode.GALLERY) {
          this._useOnlyNamesDefaultFilter = this._useOnlyNamesDefaultFilterGallery;
        } else if (action.filterMode === FilterMode.TEMP) {
          this._useOnlyNamesDefaultFilter = this._useOnlyNamesDefaultFilterTemp;
        }
        return super.reduce(state, action);
      default:
        return super.reduce(state, action);
    }
  }
}

export default new UsersStore();
